<template>
  <Drawer
    :block-scroll="true"
    :dismissable="dismissable"
    :modal="dismissable"
    :position="isFullPage ? 'full' : 'right'"
    :pt="{
      root: drawer_root_class,
      content: 'p-0 h-full w-full grow !overflow-hidden',
      closeButton: 'focus:outline-none',
      mask: { class: '!bg-transparent' },
    }"
    :showCloseIcon="closeable"
    :visible="visible"
    :baseZIndex="base_z_index"
    @update:visible="$emit('update:visible', false)"
    @after-show="$emit('after-show')"
  >
    <!-- Default header with expansion toggle -->
    <template #header>
      <slot name="header">
        <div class="flex items-center justify-between">
          <div class="flex items-center">
            <ButtonExpandCollapse
              :is_expanded="isFullPage"
              @update:expand="isFullPage = $event"
            />
            <span class="text-2xl font-semibold">
              <slot name="title">{{ title }}</slot>
            </span>
          </div>
          <slot name="header-actions" />
        </div>
      </slot>
    </template>

    <div class="flex h-full grow flex-col">
      <!-- Main content area -->
      <div
        class="grow overflow-y-auto pb-5"
        :class="padding_x"
      >
        <slot />
      </div>

      <!-- Footer -->
      <div
        v-if="show_footer"
        class="shrink-0 bg-gray-50 p-4"
      >
        <slot name="footer">
          <div class="flex flex-wrap gap-2">
            <ButtonPrimary
              ref="submitButton"
              :data-test="submit_test_id"
              :is_loading="is_loading"
              @click="handleSubmit"
            >
              {{ $t(button_primary_text) }}
            </ButtonPrimary>

            <slot name="additional-buttons" />

            <ButtonTertiary @click="$emit('update:visible', false)">
              {{ $t('form.actions.cancel') }}
            </ButtonTertiary>
          </div>
        </slot>
      </div>
    </div>
  </Drawer>
</template>

<script setup>
import { computed, onMounted, onUnmounted, provide, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import ButtonExpandCollapse from '@/app/shared_components/buttons/ButtonExpandCollapse.vue';

const { t } = useI18n();

// Singleton event handler implementation
const activeDrawers = new Set();
let globalKeydownHandler = null;

const registerDrawer = (drawer) => {
  activeDrawers.add(drawer);
  if (!globalKeydownHandler) {
    globalKeydownHandler = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
        // Find the most recently visible drawer
        const visibleDrawer = Array.from(activeDrawers).find((d) => d.props.visible && !d.props.is_loading);
        if (visibleDrawer) {
          event.preventDefault();
          event.stopPropagation();
          visibleDrawer.handleSubmit();
        }
      }
    };
    window.addEventListener('keydown', globalKeydownHandler, true);
  }
};

const unregisterDrawer = (drawer) => {
  activeDrawers.delete(drawer);
  if (activeDrawers.size === 0 && globalKeydownHandler) {
    window.removeEventListener('keydown', globalKeydownHandler, true);
    globalKeydownHandler = null;
  }
};

const props = defineProps({
  visible: {
    type: Boolean,
    required: true,
  },
  title: {
    type: String,
    default: '',
  },
  drawer_width: {
    type: String,
    default: 'lg:!w-[60rem]',
  },
  is_loading: {
    type: Boolean,
    default: false,
  },
  submit_test_id: {
    type: String,
    default: 'drawer-submit',
  },
  padding_x: {
    type: String,
    default: 'px-5',
  },
  closeable: {
    type: Boolean,
    default: true,
  },
  button_primary_text: {
    type: String,
    default: 'form.actions.save',
  },
  show_footer: {
    type: Boolean,
    default: true,
  },
  base_z_index: {
    type: Number,
    default: 0,
  },
  dismissable: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:visible', 'submit', 'after-show']);

const isFullPage = ref(false);

provide('drawerIsFullPage', isFullPage);

const drawer_root_class = computed(() => {
  if (isFullPage.value) {
    return '!w-screen lg:!w-screen !max-w-none !h-screen';
  }
  return `${props.drawer_width} !w-full`;
});

const handleSubmit = () => {
  if (!props.is_loading) {
    emit('submit');
  }
};

const drawer_ref = { props, handleSubmit };

onMounted(() => {
  registerDrawer(drawer_ref);

  // Prevent the drawer from focusing on the close button
  if (props.visible) {
    // Use a small delay to ensure the drawer is fully mounted
    setTimeout(() => {
      const closeButton = document.querySelector('.p-drawer-close-button');
      if (closeButton) {
        closeButton.setAttribute('tabindex', '-1');
      }
    }, 50);
  }
});

onUnmounted(() => {
  unregisterDrawer(drawer_ref);
});
</script>
